'use client';
import React, { useState, useEffect } from 'react';

const footerLinks = [
  {
    heading: 'Services',
    links: [
      { label: 'Bookkeeping', href: '#services' },
      { label: 'Bank Reconciliation', href: '#services' },
      { label: 'Financial Statements', href: '#services' },
      { label: 'Service Estimate', href: '#estimate' },
    ],
  },
  {
    heading: 'Engagement',
    links: [
      { label: 'How It Works', href: '#process' },
      { label: 'Client Intake Portal', href: '#portal' },
      { label: 'About SYHJ', href: '#about' },
    ],
  },
  {
    heading: 'Contact',
    links: [
      { label: 'syhj.services@email.com', href: 'mailto:syhj.services@email.com' },
      { label: 'LinkedIn', href: '#' },
    ],
  },
];

export default function Footer() {
  const [year, setYear] = useState('2025');

  useEffect(() => {
    setYear(String(new Date().getFullYear()));
  }, []);

  const handleNavClick = (href: string) => {
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-brand-black text-white">
      {/* Top CTA bar */}
      <div
        className="border-b border-white/10"
        style={{ background: 'linear-gradient(90deg, #fe8303, #fd5108)' }}
      >
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6">
          <div>
            <p
              className="text-white font-bold text-base sm:text-lg"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Ready to get your books in order?
            </p>
            <p
              className="text-white/80 text-sm mt-1"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
            >
              Start with a free estimate or submit an intake form today.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={() => handleNavClick('#estimate')}
              className="bg-white text-primary font-bold px-6 py-3 text-sm hover:bg-white/90 transition-colors duration-200 cursor-pointer touch-manipulation text-center"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px' }}
            >
              Get Estimate
            </button>
            <button
              onClick={() => handleNavClick('#portal')}
              className="border border-white text-white font-bold px-6 py-3 text-sm hover:bg-white/10 transition-colors duration-200 cursor-pointer touch-manipulation text-center"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px' }}
            >
              Client Portal
            </button>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-16">
          {/* Brand column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-5 sm:mb-6">
              <span
                className="font-serif text-2xl font-bold text-white tracking-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '-1px' }}
              >
                SYHJ
              </span>
              <span className="w-2 h-2 rounded-full bg-primary" aria-hidden="true" />
            </div>
            <p
              className="text-white/55 text-sm leading-relaxed mb-5 sm:mb-6"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.7' }}
            >
              Professional freelance bookkeeping and financial services. CMA-level methodology, transparent pricing, and reliable delivery.
            </p>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-primary flex items-center justify-center flex-shrink-0">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M6 1l1.5 4H11L8 7.5l1.5 4L6 9l-3.5 2.5L4 7.5 1 5h3.5L6 1z" fill="white" />
                </svg>
              </div>
              <span
                className="text-white/55 text-xs"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
              >
                US CMA Candidate &middot; Part 2 Cleared
              </span>
            </div>
          </div>

          {/* Link columns */}
          {footerLinks.map((col) => (
            <div key={col.heading}>
              <h5
                className="text-white font-semibold text-sm mb-4 sm:mb-5 tracking-widest uppercase"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", letterSpacing: '2px' }}
              >
                {col.heading}
              </h5>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith('mailto') ? (
                      <a
                        href={link.href}
                        className="text-white/55 text-sm hover:text-white transition-colors duration-200 break-all"
                        style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <button
                        onClick={() => handleNavClick(link.href)}
                        className="text-white/55 text-sm hover:text-white transition-colors duration-200 bg-transparent border-none cursor-pointer text-left touch-manipulation"
                        style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '36px' }}
                      >
                        {link.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12 py-5 sm:py-6 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3 text-center sm:text-left">
          <p
            className="text-white/35 text-xs"
            style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
          >
            &copy; {year} SYHJ Financial Services. All rights reserved.
          </p>
          <p
            className="text-white/35 text-xs"
            style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
          >
            SYHJ is a freelance practice. Not a licensed CPA firm.
          </p>
        </div>
      </div>
    </footer>
  );
}
